/**
 * Utility functions for error handling and classification
 */

import { ApiError } from "@/api";

export type ErrorType = '404' | 'network' | 'server' | 'upload' | 'payment' | 'default' | 'processing' | 'api' | 'download';

/**
 * Common error messages for different scenarios
 */
export const ERROR_MESSAGES = {
  NETWORK_TIMEOUT: 'Request timed out. Please check your internet connection.',
  FILE_TOO_LARGE: 'File size is too large. Please select a smaller image.',
  INVALID_FILE_TYPE: 'Invalid file type. Please select a valid image file.',
  PAYMENT_FAILED: 'Payment could not be processed. Please try again.',
  SERVER_MAINTENANCE: 'Our servers are currently under maintenance. Please try again later.',
  UPLOAD_FAILED: 'Failed to upload your photo. Please try again.',
  PROCESSING_FAILED: 'Failed to process your image. Please try again with a different photo.',
} as const;

/**
 * Error boundary helper for React components
 */
export class AppError extends Error {
  public readonly type: ErrorType;
  public readonly userMessage?: string;

  constructor(message: string, type: ErrorType = 'default', userMessage?: string) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.userMessage = userMessage;
  }
}

/**
 * Handle API errors and convert them to appropriate error types
 */
export const handleApiError = (error: unknown): { type: ErrorType; message: string } => {

  if (error instanceof ApiError) {
    return {
      type: 'api',
      message: error.body.error.userMessage || error.body.error.message || error.message || 'An unexpected error occurred.'
    }
  }

  if (error instanceof AppError) {
    return {
      type: error.type,
      message: error.userMessage || error.message
    };
  }

  if (error instanceof Error) {
    // Network errors
    if (error.message.includes('fetch') || error.message.includes('network')) {
      return {
        type: 'network',
        message: ERROR_MESSAGES.NETWORK_TIMEOUT
      };
    }

    // Server errors (5xx)
    if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
      return {
        type: 'server',
        message: ERROR_MESSAGES.SERVER_MAINTENANCE
      };
    }

    // Upload errors
    if (error.message.includes('upload') || error.message.includes('file')) {
      return {
        type: 'upload',
        message: ERROR_MESSAGES.UPLOAD_FAILED
      };
    }

    // Payment errors
    if (error.message.includes('payment') || error.message.includes('billing')) {
      return {
        type: 'payment',
        message: ERROR_MESSAGES.PAYMENT_FAILED
      };
    }

    // processing errors
    if (error.message.includes('processing')) {
      return {
        type: 'processing',
        message: ERROR_MESSAGES.PROCESSING_FAILED
      };
    }
  }

  // Default error
  return {
    type: 'default',
    message: 'An unexpected error occurred. Please try again.'
  };
};
